import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import { communityGroupMutations } from "@/queries/community-groups/mutations";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { type CreateOrEditResidentGroupFormValues, ResidentGroupForm } from "./ResidentGroupForm";

export function EditResidentGroupPage(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useFlashToast();
  const { id } = useParams<{ id: string }>();
  const queries = useCommunityGroupQueries();
  const query = useQuery({ ...queries.details({ groupId: id! }, { translationMode: "original" }), gcTime: 0 });
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const mutation = communityGroupMutations.useUpdateResidentGroup();
  async function onSubmit(request: CreateOrEditResidentGroupFormValues) {
    if (!request.image[0]) {
      toast({ variant: "alert", message: t("page.create-resident-group.create.notification.failed") });
      throw new Error("Image not provided");
    }
    const image = await uploadFormImage(request.image[0]);
    if (image) {
      mutation.mutate(
        { groupId: id!, data: { name: request.name, description: request.description, imageId: image.id } },
        { onSuccess: () => void navigate(routes.interestGroups.details({ slug, id: id! })) },
      );
    }
  }
  const group = query.data;
  if (group && (!group.canEdit || !group.isResidentGroup)) {
    return <ErrorPage status={403} />;
  }
  if (query.error) {
    return <ErrorPage error={query.error} />;
  }
  if (query.isFetching) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={t("page.create-resident-group.edit.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            { name: t("page.group-details.breadcrumbs.discovery") },
            { name: t("page.interest-group.list.title"), to: routes.interestGroups.list({ slug }) },
            { name: group?.name || "" },
          ]}
        />
      }
    >
      <Form.Container>
        <ResidentGroupForm
          contentLanguageId={group?.originalLanguageId}
          defaultValues={{
            name: group?.name,
            description: group?.description,
            image: group?.image ? [group.image] : [],
          }}
          isEditMode
          isSubmitting={mutation.isPending || isUploadingImage}
          onSubmit={onSubmit}
        />
      </Form.Container>
    </DocumentPage>
  );
}
